import { ActionIcon, Alert, Button, Group, Menu, Modal, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdPlatform } from '@marketing-tool/shared';
import type { Line } from '@marketing-tool/shared';
import { IconCopy, IconDotsVertical, IconExternalLink, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { StatusBadge } from '../../components/StatusBadge';
import { Table } from '../../components/Table';
import { ThumbnailPreview } from '../../components/ThumbnailPreview';
import { useAds } from '../../data/game-report';
import type { Ad, AdGroup, Campaign } from '../../data/game-report/types';
import type { Game } from '../../data/games';
import * as remote from '../../data/remote-data';
import { trpc } from '../../lib/trpc';
import { plural } from '../../lib/utils';
import { BulkEditModal } from '../../platforms/line/components/BulkEditModal';
import type { BulkEditFormData } from '../../platforms/line/components/BulkEditModal';
import { CreateAdForm } from '../../platforms/line/components/CreateAdForm';
import type { CreateAdFormData } from '../../platforms/line/components/CreateAdForm';
import { EditAdForm } from '../../platforms/line/components/EditAdForm';
import type { EditAdFormData } from '../../platforms/line/components/EditAdForm';

import { confirmAndDeleteTrpc, OperationErrorAlert } from './mutations';
import type { OperationError } from './mutations';

type AdsTabContentProps = {
    gameId: string;
    platform: AdPlatform;
    campaignId?: string;
    adGroupId?: string;
    selectedCampaign: Campaign | undefined;
    selectedAdGroup: AdGroup | undefined;
    game: Game;
};

export const AdsTabContent = ({
    gameId,
    platform,
    campaignId,
    adGroupId,
    selectedCampaign,
    selectedAdGroup,
    game,
}: AdsTabContentProps) => {
    const ads = useAds(gameId, platform, campaignId, adGroupId);

    // --- Modal state ---
    const [createModal, setCreateModal] = useState<{ source?: Ad } | null>(null);
    const [editing, setEditing] = useState<Ad | null>(null);
    const [bulkEditing, setBulkEditing] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

    // --- Per-operation error/loading state ---
    const [createError, setCreateError] = useState<OperationError>(null);
    const [editError, setEditError] = useState<OperationError>(null);
    const [bulkEditError, setBulkEditError] = useState<OperationError>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Handlers ---

    const closeCreate = () => {
        setCreateModal(null);
        setCreateError(null);
    };

    const handleCreate = async (data: CreateAdFormData) => {
        setIsSubmitting(true);
        setCreateError(null);
        try {
            // Ad creation is LINE-only (Meta doesn't support it)
            const batchResult = await trpc.line.ads.create.mutate({
                gameId,
                adGroupId: data.adGroupId,
                creativeMediaIds: data.creativeMediaIds,
                title: data.title,
                description: data.description,
                landingPageUrl: data.landingPageUrl,
            });

            const results = batchResult.results || [];
            const successCount = batchResult.successCount ?? results.filter((r) => r.success).length;
            const failCount = batchResult.failCount ?? results.length - successCount;

            if (failCount === 0) {
                notifications.show({
                    title: 'Success',
                    message: `${successCount} ${plural(successCount, 'ad', 'ads')} created`,
                    color: 'green',
                });
                closeCreate();
                void ads.load();
            } else {
                const failedResults = results.filter((r) => !r.success);
                const failedDetails = failedResults.map((r) => `${r.creativeName}: ${r.error}`).join('\n');
                const messagePrefix =
                    successCount > 0
                        ? `Created ${successCount} ${plural(successCount, 'ad', 'ads')}, but failed to create ${failCount} ${plural(failCount, 'ad', 'ads')}`
                        : `Failed to create ${failCount} ${plural(failCount, 'ad', 'ads')}`;
                setCreateError({ message: messagePrefix, details: failedDetails });
                if (successCount > 0) {
                    void ads.load();
                    setCreateModal(null);
                }
            }
        } catch (error) {
            setCreateError({
                message: error instanceof Error ? error.message : 'Failed to create ads. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeEdit = () => {
        setEditing(null);
        setEditError(null);
    };

    const handleUpdate = async (data: EditAdFormData) => {
        setIsSubmitting(true);
        setEditError(null);
        try {
            const { id, ...updateData } = data;
            // Ad updates are LINE-only (Meta doesn't support it)
            await trpc.line.ads.update.mutate({
                gameId,
                updates: { [id]: updateData },
            });
            notifications.show({ title: 'Success', message: 'Ad updated', color: 'green' });
            closeEdit();
            void ads.load();
        } catch (error) {
            setEditError({
                message: error instanceof Error ? error.message : 'Failed to update ad. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (adId: string | number) => {
        // Ad deletion is LINE-only (Meta doesn't support it)
        confirmAndDeleteTrpc({
            title: 'Delete ad',
            message: 'Are you sure you want to delete this ad? This action cannot be undone.',
            deleteFn: () => trpc.line.ads.delete.mutate({ gameId, adIds: [String(adId)] }),
            successMessage: 'Ad deleted',
            onSuccess: () => void ads.load(),
        });
    };

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        // Ad deletion is LINE-only (Meta doesn't support it)
        confirmAndDeleteTrpc({
            title: 'Delete ads',
            message: `Are you sure you want to delete ${selectedIds.size} ad(s)? This action cannot be undone.`,
            deleteFn: () => trpc.line.ads.delete.mutate({ gameId, adIds: Array.from(selectedIds).map(String) }),
            successMessage: `${selectedIds.size} ad(s) deleted`,
            onSuccess: () => {
                setSelectedIds(new Set());
                void ads.load();
            },
        });
    };

    const closeBulkEdit = () => {
        setBulkEditing(false);
        setBulkEditError(null);
    };

    const handleBulkUpdate = async (data: BulkEditFormData) => {
        setIsSubmitting(true);
        setBulkEditError(null);
        try {
            const updates = Object.fromEntries(Array.from(selectedIds).map((id) => [id, data]));
            // Ad updates are LINE-only (Meta doesn't support it)
            await trpc.line.ads.update.mutate({ gameId, updates });
            notifications.show({ title: 'Success', message: 'Ads updated', color: 'green' });
            closeBulkEdit();
            setSelectedIds(new Set());
            void ads.load();
        } catch (error) {
            setBulkEditError({
                message: error instanceof Error ? error.message : 'Failed to update ads. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render ---

    if (remote.isNotAsked(ads.data)) return null;
    if (remote.isFailure(ads.data)) {
        return (
            <Alert color="red" title="Error loading ads">
                {ads.data.value}
            </Alert>
        );
    }

    return (
        <>
            <Group justify="space-between" mb="md">
                <Text size="sm" fw={600}>
                    Ads
                </Text>
                {!selectedCampaign || !selectedAdGroup ? (
                    <Tooltip
                        label={
                            !selectedCampaign
                                ? 'Select a campaign first to create an ad'
                                : 'Select an ad group first to create an ad'
                        }
                    >
                        <span>
                            <Button
                                size="sm"
                                onClick={() => setCreateModal({})}
                                disabled
                                leftSection={<IconPlus size={16} />}
                            >
                                Create Ad
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Button size="sm" onClick={() => setCreateModal({})} leftSection={<IconPlus size={16} />}>
                        Create Ad
                    </Button>
                )}
            </Group>

            <Table
                storageKey="campaign-ads"
                loading={remote.isLoading(ads.data)}
                id={(ad) => ad.id}
                density="xs"
                columns={[
                    ...(platform === AdPlatform.Line
                        ? [
                              {
                                  name: 'Thumbnail',
                                  alt: 'Creative thumbnail',
                                  type: 'thumbnail' as const,
                                  width: 56,
                                  render: (ad: Ad) => {
                                      if (ad.platform !== AdPlatform.Line || !ad.creative) return null;
                                      const { video, image, animation } = ad.creative;
                                      const getMediaUrl = (media?: Line.EmbeddedMedia) =>
                                          media?.url || media?.object?.sourceUrl;
                                      const videoUrl = getMediaUrl(video);
                                      const imageUrl = getMediaUrl(image);
                                      const animationUrl = getMediaUrl(animation);
                                      if (videoUrl) return <ThumbnailPreview url={videoUrl} isVideo alt={ad.name} />;
                                      if (imageUrl) return <ThumbnailPreview url={imageUrl} alt={ad.name} />;
                                      if (animationUrl) return <ThumbnailPreview url={animationUrl} alt={ad.name} />;
                                      return null;
                                  },
                              },
                          ]
                        : []),
                    {
                        name: 'Ad',
                        render: (ad) => (
                            <Text fw={500} inherit>
                                {ad.name}
                            </Text>
                        ),
                        sort: (a, b) => a.name.localeCompare(b.name),
                    },
                    {
                        name: 'Status',
                        type: 'status',
                        width: 120,
                        render: (ad) => {
                            const status = 'deliveryStatus' in ad ? ad.deliveryStatus : ad.effectiveStatus;
                            return <StatusBadge status={status} />;
                        },
                        sort: (a, b) => {
                            const statusA = 'deliveryStatus' in a ? a.deliveryStatus : a.effectiveStatus;
                            const statusB = 'deliveryStatus' in b ? b.deliveryStatus : b.effectiveStatus;
                            return statusA.localeCompare(statusB);
                        },
                    },
                    ...(platform === AdPlatform.Line
                        ? [
                              {
                                  name: 'Creative Format',
                                  width: 180,
                                  render: (ad: Ad) => {
                                      if (ad.platform === AdPlatform.Line && ad.creative) {
                                          return ad.creative.creativeFormat;
                                      }
                                      return '-';
                                  },
                              },
                          ]
                        : []),
                    {
                        name: 'Creation Date',
                        type: 'date',
                        width: 120,
                        render: (ad) => new Date(ad.createdDate).toLocaleDateString(),
                        sort: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
                    },
                    {
                        name: 'Actions',
                        type: 'actions',
                        width: 50,
                        render: (ad) => (
                            <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                    <ActionIcon
                                        variant="subtle"
                                        aria-label="Row actions"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconDotsVertical size={16} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {ad.externalAdsManagerUrl && (
                                        <Menu.Item
                                            component="a"
                                            href={ad.externalAdsManagerUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            leftSection={<IconExternalLink size={14} />}
                                        >
                                            Open in Ads Manager
                                        </Menu.Item>
                                    )}
                                    <Menu.Item
                                        leftSection={<IconCopy size={14} />}
                                        onClick={() => setCreateModal({ source: ad })}
                                    >
                                        Duplicate
                                    </Menu.Item>
                                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditing(ad)}>
                                        Edit
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item
                                        leftSection={<IconTrash size={14} />}
                                        color="red"
                                        onClick={() => void handleDelete(ad.id)}
                                    >
                                        Delete
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        ),
                    },
                ]}
                data={remote.withDefault(ads.data, [])}
                defaultSortColumn="Ad"
                pagination={{ pageSize: 20, showPagination: true }}
                selection={{
                    selectedIds,
                    onSelectionChange: setSelectedIds,
                    actions: [
                        {
                            label: 'Edit Selected',
                            onClick: () => setBulkEditing(true),
                            variant: 'outline',
                            startIcon: <IconPencil size={16} />,
                        },
                        {
                            label: 'Delete Selected',
                            onClick: handleBulkDelete,
                            variant: 'filled',
                            color: 'red',
                            startIcon: <IconTrash size={16} />,
                        },
                    ],
                }}
            />

            {/* Create / Duplicate Ad */}
            <Modal
                opened={createModal !== null}
                onClose={closeCreate}
                size="lg"
                title={createModal?.source ? 'Duplicate Ad' : 'Create Ad'}
            >
                <OperationErrorAlert error={createError} title="Failed to create ad" />
                {platform && selectedCampaign && selectedAdGroup && (
                    <CreateAdForm
                        platform={platform}
                        adGroup={selectedAdGroup}
                        campaign={selectedCampaign}
                        game={game}
                        onSubmit={handleCreate}
                        onCancel={closeCreate}
                        isSubmitting={isSubmitting}
                        initialData={
                            createModal?.source
                                ? {
                                      name: `${createModal.source.name} (Copy)`,
                                      title:
                                          createModal.source.platform === AdPlatform.Line &&
                                          createModal.source.creative?.title
                                              ? createModal.source.creative.title
                                              : '',
                                      description:
                                          createModal.source.platform === AdPlatform.Line &&
                                          createModal.source.creative?.description
                                              ? createModal.source.creative.description
                                              : '',
                                  }
                                : undefined
                        }
                    />
                )}
            </Modal>

            {/* Edit Ad */}
            <Modal opened={editing !== null} onClose={closeEdit} size="lg" title="Edit Ad">
                <OperationErrorAlert error={editError} title="Failed to update ad" />
                {platform && editing && (
                    <EditAdForm
                        platform={platform}
                        ad={editing}
                        onSubmit={handleUpdate}
                        onCancel={closeEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>

            {/* Bulk Edit Ads */}
            <Modal opened={bulkEditing} onClose={closeBulkEdit} size="lg" title="Bulk Edit Ads">
                <OperationErrorAlert error={bulkEditError} title="Failed to update ads" />
                {platform && (
                    <BulkEditModal
                        platform={platform}
                        entityType="ad"
                        selectedCount={selectedIds.size}
                        onSubmit={handleBulkUpdate}
                        onCancel={closeBulkEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>
        </>
    );
};
